import React, { useEffect, useState } from 'react';
import { peopleProfiles, linkedInPosts } from '../utils/mockData';
import { X as XIcon, MessageSquare as MessageSquareIcon, ThumbsUp as ThumbsUpIcon, Share as ShareIcon, Tag as TagIcon, Mail as MailIcon, Phone as PhoneIcon, Briefcase as BriefcaseIcon, MapPin as MapPinIcon, Link as LinkIcon, Star as StarIcon, Clock as ClockIcon, CheckCircle as CheckCircleIcon, XCircle as XCircleIcon } from 'lucide-react';
interface PersonModalProps {
  personId: number;
  onClose: () => void;
  onNavigateToMessages: (personId: number) => void;
  onMarkInteresting?: (personId: number) => void;
  onMarkNotRelevant?: (personId: number) => void;
}
export const PersonModal: React.FC<PersonModalProps> = ({
  personId,
  onClose,
  onNavigateToMessages,
  onMarkInteresting,
  onMarkNotRelevant
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const person = peopleProfiles.find(p => p.id === personId);
  useEffect(() => {
    // Simulate loading
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [personId]);
  if (!person) return null;
  const getPostById = (postId: number) => {
    return linkedInPosts.find(post => post.id === postId);
  };
  const calculateEngagementScore = () => {
    const baseScore = Math.min(person.interactions.length * 2, 6);
    const recencyBonus = person.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 2) ? 2 : 0;
    const commentBonus = person.interactions.some(i => i.comment) ? 2 : 0;
    return Math.min(baseScore + recencyBonus + commentBonus, 10);
  };
  const engagementScore = calculateEngagementScore();
  // Additional profile data
  const profileData = {
    email: `${person.name.toLowerCase().replace(' ', '.')}@${person.company.toLowerCase().replace(' ', '')}.com`,
    phone: '+1 (555) 123-4567',
    location: 'San Francisco, CA',
    website: `https://www.linkedin.com/in/${person.name.toLowerCase().replace(' ', '-')}`,
    connectionDate: 'Connected 3 months ago',
    mutualConnections: 8,
    notes: 'Met at SaaS Conference 2023. Interested in AI solutions for marketing teams.',
    recentActivity: [{
      type: 'post',
      date: '2 days ago',
      content: 'Shared an article about AI in marketing'
    }, {
      type: 'comment',
      date: '1 week ago',
      content: 'Commented on industry trends'
    }, {
      type: 'job',
      date: '3 months ago',
      content: 'Started new position at ' + person.company
    }]
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-879">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-880">
        <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-881">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center" data-id="element-882">
            {person.name}
            {person.relevance === 'high' && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-883">
                <StarIcon size={12} className="mr-1" data-id="element-884" />
                High Intent
              </span>}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-885">
            <XIcon size={20} data-id="element-886" />
          </button>
        </div>
        <div className="border-b border-gray-200" data-id="element-887">
          <nav className="-mb-px flex space-x-8 px-4" data-id="element-888">
            <button onClick={() => setActiveTab('profile')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'profile' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-889">
              Profile
            </button>
            <button onClick={() => setActiveTab('engagement')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'engagement' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-890">
              Engagement History
            </button>
            <button onClick={() => setActiveTab('insights')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'insights' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-891">
              Intent Insights
            </button>
          </nav>
        </div>
        <div className="flex-1 overflow-y-auto p-4" data-id="element-892">
          {loading ? <div className="animate-pulse space-y-4" data-id="element-893">
              <div className="h-32 bg-gray-200 rounded" data-id="element-894"></div>
              <div className="h-20 bg-gray-200 rounded" data-id="element-895"></div>
              <div className="h-24 bg-gray-200 rounded" data-id="element-896"></div>
              <div className="h-16 bg-gray-200 rounded" data-id="element-897"></div>
            </div> : <>
              {activeTab === 'profile' && <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-898">
                  <div className="md:col-span-1" data-id="element-899">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-900">
                      <div className="flex flex-col items-center mb-4" data-id="element-901">
                        <img src={person.image} alt={person.name} className="w-24 h-24 rounded-full mb-3 border-2 border-gray-200" data-id="element-902" />
                        <h3 className="text-lg font-medium text-gray-900 text-center" data-id="element-903">
                          {person.name}
                        </h3>
                        <p className="text-gray-500 text-center" data-id="element-904">
                          {person.title}
                        </p>
                        <p className="text-gray-500 text-center mb-3" data-id="element-905">
                          {person.company}
                        </p>
                        <div className="flex space-x-3" data-id="element-906">
                          <div className="flex flex-col items-center" data-id="element-907">
                            <div className="text-xs text-gray-500" data-id="element-908">
                              Engagement
                            </div>
                            <div className={`text-sm font-medium px-2 py-1 rounded ${engagementScore >= 8 ? 'bg-green-100 text-green-700' : engagementScore >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-909">
                              {engagementScore}/10
                            </div>
                          </div>
                          <div className="flex flex-col items-center" data-id="element-910">
                            <div className="text-xs text-gray-500" data-id="element-911">
                              Relevance
                            </div>
                            <div className={`text-sm font-medium px-2 py-1 rounded ${person.relevance === 'high' ? 'bg-green-100 text-green-700' : person.relevance === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-912">
                              {person.relevance.charAt(0).toUpperCase() + person.relevance.slice(1)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-913">
                        <div className="flex items-center" data-id="element-914">
                          <MailIcon size={16} className="text-gray-400 mr-2" data-id="element-915" />
                          <span className="text-sm" data-id="element-916">{profileData.email}</span>
                        </div>
                        <div className="flex items-center" data-id="element-917">
                          <PhoneIcon size={16} className="text-gray-400 mr-2" data-id="element-918" />
                          <span className="text-sm" data-id="element-919">{profileData.phone}</span>
                        </div>
                        <div className="flex items-center" data-id="element-920">
                          <BriefcaseIcon size={16} className="text-gray-400 mr-2" data-id="element-921" />
                          <span className="text-sm" data-id="element-922">
                            {person.title} at {person.company}
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-923">
                          <MapPinIcon size={16} className="text-gray-400 mr-2" data-id="element-924" />
                          <span className="text-sm" data-id="element-925">
                            {profileData.location}
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-926">
                          <LinkIcon size={16} className="text-gray-400 mr-2" data-id="element-927" />
                          <a href="#" className="text-sm text-blue-600 hover:underline" data-id="element-928">
                            LinkedIn Profile
                          </a>
                        </div>
                        <div className="flex items-center" data-id="element-929">
                          <ClockIcon size={16} className="text-gray-400 mr-2" data-id="element-930" />
                          <span className="text-sm" data-id="element-931">
                            {profileData.connectionDate}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-932">
                      <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-933">
                        Tags & Interests
                      </h4>
                      <div className="flex flex-wrap gap-2" data-id="element-934">
                        {person.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-935">
                            <TagIcon size={12} className="mr-1" data-id="element-936" />
                            {tag}
                          </span>)}
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2" data-id="element-937">
                    <div className="mb-6" data-id="element-938">
                      <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-939">
                        Recent Activity
                      </h4>
                      <div className="space-y-3" data-id="element-940">
                        {profileData.recentActivity.map((activity, index) => <div key={index} className="p-3 bg-gray-50 rounded-md border border-gray-200" data-id="element-941">
                            <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-942">
                              {activity.type === 'post' ? <ShareIcon size={14} className="mr-1 text-blue-600" data-id="element-943" /> : activity.type === 'comment' ? <MessageSquareIcon size={14} className="mr-1 text-purple-600" data-id="element-944" /> : <BriefcaseIcon size={14} className="mr-1 text-green-600" data-id="element-945" />}
                              <span data-id="element-946">{activity.date}</span>
                            </div>
                            <p className="text-sm" data-id="element-947">{activity.content}</p>
                          </div>)}
                      </div>
                    </div>
                    <div className="mb-6" data-id="element-948">
                      <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-949">
                        Notes
                      </h4>
                      <div className="p-3 bg-gray-50 rounded-md border border-gray-200" data-id="element-950">
                        <p className="text-sm" data-id="element-951">{profileData.notes}</p>
                      </div>
                    </div>
                    <div className="mb-6" data-id="element-952">
                      <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-953">
                        Mutual Connections
                      </h4>
                      <div className="p-3 bg-gray-50 rounded-md border border-gray-200 flex items-center" data-id="element-954">
                        <div className="flex -space-x-2 mr-3" data-id="element-955">
                          {[...Array(3)].map((_, i) => <div key={i} className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600" data-id="element-956">
                              {String.fromCharCode(65 + i)}
                            </div>)}
                        </div>
                        <div className="text-sm" data-id="element-957">
                          <span className="font-medium" data-id="element-958">
                            {profileData.mutualConnections}
                          </span>{' '}
                          mutual connections
                        </div>
                      </div>
                    </div>
                    <div data-id="element-959">
                      <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-960">
                        Recommended Actions
                      </h4>
                      <div className="p-4 bg-blue-50 border border-blue-100 rounded-md" data-id="element-961">
                        <h5 className="font-medium text-blue-800 mb-2" data-id="element-962">
                          Engagement opportunity
                        </h5>
                        <p className="text-sm text-blue-700 mb-3" data-id="element-963">
                          {person.name} has recently engaged with content about{' '}
                          {person.tags[0]}. Consider reaching out with a
                          personalized message about this topic.
                        </p>
                        <div className="flex space-x-2" data-id="element-964">
                          <button onClick={() => onNavigateToMessages(personId)} className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200" data-id="element-965">
                            <MessageSquareIcon size={12} className="mr-1" data-id="element-966" />
                            Create Message
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
              {activeTab === 'engagement' && <div data-id="element-967">
                  <div className="flex items-center justify-between mb-4" data-id="element-968">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-969">
                      Engagement History
                    </h3>
                    <div className="flex items-center" data-id="element-970">
                      <span className="text-sm text-gray-500 mr-2" data-id="element-971">
                        Total Interactions:
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-972">
                        {person.interactions.length}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-6" data-id="element-973">
                    {person.interactions.map((interaction, index) => {
                const post = getPostById(interaction.postId);
                if (!post) return null;
                return <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-974">
                          <div className="flex items-center mb-3" data-id="element-975">
                            <img src={post.authorImage} alt={post.author} className="w-10 h-10 rounded-full mr-3" data-id="element-976" />
                            <div data-id="element-977">
                              <div className="font-medium text-gray-900" data-id="element-978">
                                {post.author}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-979">
                                {post.authorTitle}
                              </div>
                            </div>
                            <div className="ml-auto text-sm text-gray-500" data-id="element-980">
                              {post.date}
                            </div>
                          </div>
                          <div className="text-gray-800 mb-4 line-clamp-3" data-id="element-981">
                            {post.content}
                          </div>
                          <div className="pt-3 border-t border-gray-100" data-id="element-982">
                            <div className="flex items-center text-sm text-gray-600 mb-2" data-id="element-983">
                              {interaction.type === 'liked' ? <>
                                  <ThumbsUpIcon size={16} className="text-blue-600 mr-2" data-id="element-984" />
                                  <span data-id="element-985">
                                    {person.name} liked this post{' '}
                                    {interaction.date}
                                  </span>
                                </> : interaction.type === 'commented' ? <>
                                  <MessageSquareIcon size={16} className="text-purple-600 mr-2" data-id="element-986" />
                                  <span data-id="element-987">
                                    {person.name} commented on this post{' '}
                                    {interaction.date}
                                  </span>
                                </> : <>
                                  <ShareIcon size={16} className="text-green-600 mr-2" data-id="element-988" />
                                  <span data-id="element-989">
                                    {person.name} shared this post{' '}
                                    {interaction.date}
                                  </span>
                                </>}
                            </div>
                            {interaction.comment && <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200" data-id="element-990">
                                <p className="text-sm italic" data-id="element-991">
                                  "{interaction.comment}"
                                </p>
                              </div>}
                          </div>
                        </div>;
              })}
                  </div>
                  {person.interactions.length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-992">
                      <MessageSquareIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-993" />
                      <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-994">
                        No engagement history
                      </h3>
                      <p className="text-gray-500" data-id="element-995">
                        This person hasn't interacted with your content yet.
                      </p>
                    </div>}
                </div>}
              {activeTab === 'insights' && <div data-id="element-996">
                  <div className="flex items-center justify-between mb-4" data-id="element-997">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-998">
                      Intent Insights
                    </h3>
                    <div className="flex items-center" data-id="element-999">
                      <span className="text-sm text-gray-500 mr-2" data-id="element-1000">
                        Overall Intent:
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${person.relevance === 'high' ? 'bg-green-100 text-green-800' : person.relevance === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1001">
                        {person.relevance.charAt(0).toUpperCase() + person.relevance.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1002">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1003">
                      <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1004">
                        Engagement Metrics
                      </h4>
                      <div className="space-y-3" data-id="element-1005">
                        <div data-id="element-1006">
                          <div className="flex justify-between text-sm mb-1" data-id="element-1007">
                            <span className="text-gray-600" data-id="element-1008">
                              Engagement Frequency
                            </span>
                            <span className="font-medium" data-id="element-1009">
                              {person.interactions.length} interactions
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1010">
                            <div className="bg-blue-600 h-2 rounded-full" style={{
                        width: `${Math.min(person.interactions.length * 10, 100)}%`
                      }} data-id="element-1011"></div>
                          </div>
                        </div>
                        <div data-id="element-1012">
                          <div className="flex justify-between text-sm mb-1" data-id="element-1013">
                            <span className="text-gray-600" data-id="element-1014">Recency</span>
                            <span className="font-medium" data-id="element-1015">
                              {person.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 3) ? 'Very Recent' : person.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 7) ? 'Recent' : 'Not Recent'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1016">
                            <div className={`h-2 rounded-full ${person.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 3) ? 'bg-green-600 w-full' : person.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 7) ? 'bg-yellow-500 w-2/3' : 'bg-red-500 w-1/3'}`} data-id="element-1017"></div>
                          </div>
                        </div>
                        <div data-id="element-1018">
                          <div className="flex justify-between text-sm mb-1" data-id="element-1019">
                            <span className="text-gray-600" data-id="element-1020">
                              Depth of Engagement
                            </span>
                            <span className="font-medium" data-id="element-1021">
                              {person.interactions.some(i => i.comment) ? 'Deep' : person.interactions.some(i => i.type === 'shared') ? 'Medium' : 'Surface'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1022">
                            <div className={`h-2 rounded-full ${person.interactions.some(i => i.comment) ? 'bg-green-600 w-full' : person.interactions.some(i => i.type === 'shared') ? 'bg-yellow-500 w-2/3' : 'bg-red-500 w-1/3'}`} data-id="element-1023"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1024">
                      <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1025">
                        Interest Topics
                      </h4>
                      <div className="space-y-3" data-id="element-1026">
                        {person.tags.slice(0, 3).map((tag, index) => <div key={index} data-id="element-1027">
                            <div className="flex justify-between text-sm mb-1" data-id="element-1028">
                              <span className="text-gray-600" data-id="element-1029">{tag}</span>
                              <span className="font-medium" data-id="element-1030">
                                {90 - index * 15}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1031">
                              <div className="bg-indigo-600 h-2 rounded-full" style={{
                        width: `${90 - index * 15}%`
                      }} data-id="element-1032"></div>
                            </div>
                          </div>)}
                        {person.tags.length > 3 && <div className="text-xs text-gray-500 italic" data-id="element-1033">
                            +{person.tags.length - 3} more topics
                          </div>}
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-1034">
                    <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1035">
                      Recommended Approach
                    </h4>
                    <div className="space-y-4" data-id="element-1036">
                      <div className="flex items-start" data-id="element-1037">
                        <CheckCircleIcon size={18} className="text-green-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-1038" />
                        <div data-id="element-1039">
                          <p className="text-sm font-medium" data-id="element-1040">
                            Personalized outreach referencing {person.tags[0]}
                          </p>
                          <p className="text-sm text-gray-600 mt-1" data-id="element-1041">
                            Based on their engagement patterns, they're most
                            interested in content about {person.tags[0]}.
                            Mention specific insights related to this topic.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start" data-id="element-1042">
                        <CheckCircleIcon size={18} className="text-green-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-1043" />
                        <div data-id="element-1044">
                          <p className="text-sm font-medium" data-id="element-1045">
                            Reference their recent comment
                          </p>
                          <p className="text-sm text-gray-600 mt-1" data-id="element-1046">
                            Their most engaged interaction was a comment about{' '}
                            {person.interactions[0]?.comment?.substring(0, 30)}
                            ... Acknowledge this perspective in your outreach.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start" data-id="element-1047">
                        <XCircleIcon size={18} className="text-red-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-1048" />
                        <div data-id="element-1049">
                          <p className="text-sm font-medium" data-id="element-1050">
                            Avoid generic messaging
                          </p>
                          <p className="text-sm text-gray-600 mt-1" data-id="element-1051">
                            This person has demonstrated specific interests.
                            Generic outreach is likely to be ignored.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </>}
        </div>
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between gap-3" data-id="element-1052">
          <div className="flex space-x-3" data-id="element-1053">
            <button onClick={() => onNavigateToMessages(personId)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#f9b30f] hover:bg-[#e0a40e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f9b30f]" data-id="element-1054">
              <MessageSquareIcon size={16} className="mr-2" data-id="element-1055" />
              Send Message
            </button>
          </div>
          <div className="flex space-x-3" data-id="element-1056">
            {onMarkNotRelevant && <button onClick={() => onMarkNotRelevant(personId)} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" data-id="element-1057">
                <XIcon size={16} className="mr-2" data-id="element-1058" />
                Not Relevant
              </button>}
            {onMarkInteresting && <button onClick={() => onMarkInteresting(personId)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-1059">
                <StarIcon size={16} className="mr-2" data-id="element-1060" />
                Mark Interesting
              </button>}
          </div>
        </div>
      </div>
    </div>;
};